import { useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useSetRecoilState } from "recoil"
import styled from "styled-components"

import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { useDesignContext } from "~/containers/Proposal/state/designs/design-context"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { syncWaitingForEntityState } from "~/containers/Proposal/state/sync-state"
import {
  useGetEquipmentForProposalQuery,
  NewEquipmentType,
  useUpdateStorageMutation,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { Dropdown } from "~/ui/lib/options/Select/Dropdown"

import { SetupMenuFormFooter } from "../SetupMenuFooter"

export const SingleSelectForm = styled.form`
  section {
    h3 {
      font-size: 18px;
      font-weight: 500;
    }
    p {
      font-size: 14px;
    }
  }
`

const StorageSelection: IPart[`form`] = () => {
  const { t } = useTranslation()
  const design = useDesignContext()

  const { inverter } = design.local

  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const setDesignNeedsSync = useSetRecoilState(
    syncWaitingForEntityState(`design`),
  )
  const batteriesQuery = useGetEquipmentForProposalQuery(
    client,
    {
      proposalId: design.local.proposalId,
      equipmentType: NewEquipmentType.ENERGY_BACKUP,
      inverterManufacturerId: inverter?.powurOsManufacturerId ?? null,
    },
    {
      onError,
    },
  )
  const batteries = batteriesQuery.data?.equipmentForProposal ?? []

  const updateStorage = useUpdateStorageMutation(client, {
    onError,
  })

  const batteryOptions = batteries.filter((battery) => {
    const compatibleIds = battery.batteryData?.compatibleInverterManufacturerIds
    return (
      !compatibleIds?.length ||
      compatibleIds.includes(inverter?.powurOsManufacturerId || -1)
    )
  })

  const [selectedBatteryOption, setSelectedBatteryOption] = useState<{
    name: string
    id: number
  } | null>(null)

  useEffect(() => {
    if (design.local.energyBackup) {
      setSelectedBatteryOption({
        name: design.local.energyBackup.name,
        id: design.local.energyBackup.id,
      })
    }
  }, [design.local.energyBackup])

  return (
    <SingleSelectForm>
      {inverter && inverter.manufacturer?.name === `Tesla` && (
        <section>
          <h4>Inverter/Battery Combination Selected</h4>
          <p>
            The selected inverter includes storage. To update the Storage
            Selection, return to the Inverter Selection and select a different
            inverter.
          </p>
        </section>
      )}
      {inverter && inverter.manufacturer?.name !== `Tesla` && (
        <section>
          <Dropdown.Container
            Label={() => <label>{t(`storage`)}</label>}
            SelectedOption={() => (
              <p>{selectedBatteryOption?.name ?? `Select...`}</p>
            )}
            NullableOption={
              <Dropdown.Item
                key="nullable-option"
                label1="-"
                isChecked={selectedBatteryOption === null}
                onClick={() => setSelectedBatteryOption(null)}
              />
            }
            Options={batteryOptions.map((battery) => (
              <Dropdown.Item
                key={battery.id}
                label1={battery.name}
                isChecked={battery.id === selectedBatteryOption?.id}
                onClick={async () => {
                  setSelectedBatteryOption(
                    selectedBatteryOption?.id === battery.id
                      ? null
                      : {
                          name: battery.name,
                          id: battery.id,
                        },
                  )
                }}
              />
            ))}
          />
        </section>
      )}
      <SetupMenuFormFooter
        isLoading={updateStorage.isLoading}
        isDirty={selectedBatteryOption?.id !== design?.local?.energyBackup?.id}
        onSubmit={async () => {
          await updateStorage.mutateAsync({
            input: {
              designId: design.remote.id,
              equipmentId: selectedBatteryOption?.id ?? null,
            },
          })
          setDesignNeedsSync(true)
        }}
      />
    </SingleSelectForm>
  )
}

export default StorageSelection
